import { useState, useEffect } from 'react';
import type { FAQItem } from '../../../mocks/blog-faq';

interface PostFAQProps {
  faq: FAQItem[];
  postTitle: string;
  postSlug: string;
}

export default function PostFAQ({ faq, postTitle, postSlug }: PostFAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!faq.length) return;
    const schemaId = `faq-schema-${postSlug}`;
    document.getElementById(schemaId)?.remove();

    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      name: `Perguntas frequentes sobre: ${postTitle}`,
      mainEntity: faq.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
    document.head.appendChild(script);
    return () => { document.getElementById(schemaId)?.remove(); };
  }, [faq, postTitle, postSlug]);

  if (!faq.length) return null;

  const toggle = (i: number) => setOpenIndex(prev => (prev === i ? null : i));

  return (
    <section
      aria-label="Perguntas frequentes"
      className="mt-14 rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800"
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-8 py-5 border-b border-gray-100 dark:border-gray-700"
        style={{ background: 'linear-gradient(90deg, #f8faff 0%, #ffffff 100%)' }}
      >
        <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #384774 0%, #1a3060 100%)' }}>
          <i className="ri-question-answer-fill text-white text-lg"></i>
        </div>
        <div>
          <h2 className="font-heading font-bold text-xl text-secondary dark:text-white leading-tight">
            Perguntas Frequentes
          </h2>
          <p className="font-body text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            {faq.length} perguntas respondidas por especialistas
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1.5 bg-primary/8 px-3 py-1.5 rounded-full"
          style={{ backgroundColor: 'rgba(24,199,244,0.08)' }}>
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-google-fill text-[10px]" style={{ color: '#4285F4' }}></i>
          </div>
          <span className="font-body text-[10px] font-bold text-gray-500 uppercase tracking-wide">
            Rich Snippet
          </span>
        </div>
      </div>

      {/* Accordion list */}
      <ul className="divide-y divide-gray-50 dark:divide-gray-700/50">
        {faq.map((item, i) => {
          const isOpen = openIndex === i;
          return (
            <li key={i}>
              <button
                onClick={() => toggle(i)}
                aria-expanded={isOpen}
                className="w-full flex items-start gap-4 px-8 py-5 text-left hover:bg-gray-50/60 dark:hover:bg-gray-700/30 transition-colors cursor-pointer group"
              >
                {/* Number badge */}
                <span className={`
                  flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg
                  font-heading font-bold text-xs mt-0.5 transition-colors
                  ${isOpen
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 group-hover:bg-primary/10 group-hover:text-primary'}
                `}>
                  {i + 1}
                </span>

                {/* Question */}
                <span className={`
                  flex-1 font-body font-semibold text-base leading-snug transition-colors
                  ${isOpen
                    ? 'text-primary'
                    : 'text-secondary dark:text-white group-hover:text-primary'}
                `}>
                  {item.question}
                </span>

                {/* Icon */}
                <span className={`
                  flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full mt-0.5
                  transition-all duration-300
                  ${isOpen ? 'bg-primary/10 rotate-45' : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-primary/10'}
                `}>
                  <i className={`ri-add-line text-sm transition-colors ${isOpen ? 'text-primary' : 'text-gray-400 dark:text-gray-500 group-hover:text-primary'}`}></i>
                </span>
              </button>

              {/* Answer — smooth grid-rows animation */}
              <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                <div className="overflow-hidden">
                  <div className="px-8 pb-6 pl-[4.75rem]">
                    <p className="font-body text-[15px] text-gray-600 dark:text-gray-300 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Footer hint */}
      <div className="px-8 py-4 border-t border-gray-50 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-800/50 flex items-center gap-2">
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-information-line text-xs text-gray-300 dark:text-gray-600"></i>
        </div>
        <p className="font-body text-xs text-gray-400 dark:text-gray-500">
          Mais dúvidas? Fale diretamente com nossa equipe pelo{' '}
          <a
            href="https://wa.me/5511917776227?text=Ol%C3%A1!%20Li%20o%20artigo%20e%20tenho%20uma%20d%C3%BAvida."
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary font-semibold hover:underline cursor-pointer"
          >
            WhatsApp (11) 91777-6227
          </a>
        </p>
      </div>
    </section>
  );
}
